// V3 Talent Search *******************************************************************************
// pathname: /v3/talent-search
// ************************************************************************************************
import {
  useSwrInfiniteApi,
  ApiTalentPageV3,
  ApiTalentPageV3Ok,
} from '@hooks/useSwrApi';


// Interface
// ********************************************
interface TalentSearchV3SwrProps {
  locale?: string;
  serverSideData?: ApiTalentPageV3;
  startIndex?: number;
  limit?: number;
  urlParams?: string;
}


/**
 * @description SWR: fetch talent-search v2 with infinite pagination.
 *
 * * **locale**: string. What locale to use for the request.
 * * **serverSideData**: ApiTalentPageV3, optional. The data we got from the server side request (if we do it). Default undefined.
 * * **startIndex**: number, optional. The first page number to fetch (ie: fetch starting with page 5). Default 1.
 * * **limit**: number, optional. The number of CVs to get per call. Modifies the *limit* url parameter (&limit=24). Default 24.
 * * **restUrlParams**: '&sort=relevant', optional. Default empty string.
 */
export const useSwrTalentSearchV3 = (props: TalentSearchV3SwrProps) => {
  // Props destructuring
  // ********************************************
  const {
    locale,
    serverSideData,
    startIndex = 1,
    limit = 24,
    urlParams = '',
  } = props;


  // Get data from API
  // ********************************************
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiTalentPageV3Ok>({
    url: '/v3/talent-search',
    locale,
    urlParams,
    serverSideData,
    infiniteConfig: {
      paginationType: 'page',
      limit,
      // the key where the array of items is located in the response
      responseArrayKey: 'items',
      startIndex,
    },
    apiOptions: {
      keepPreviousData: true,
      revalidateOnFocus: false,
      dedupingInterval: 1000 * 60, // 1 minute
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    // We need to account for the case when the data the last page perfectly fits the limit, so we use the pages property
    // if we reached the end (page === pages), we don't show the load more button (showLoadMore = false)
    showLoadMore: showLoadMore && data?.at(-1)?.page !== data?.at(-1)?.pages,
    sizeAsRef,
  };
};
